/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './hospital-institution.reducer';

export const HospitalInstitution = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const hospitalInstitutionList = useAppSelector(state => state.hospitalInstitution.entities);
  const loading = useAppSelector(state => state.hospitalInstitution.loading);
  const totalItems = useAppSelector(state => state.hospitalInstitution.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="hospital-institution-heading" data-cy="HospitalInstitutionHeading">
        <Translate contentKey="tinyMedicRestApp.hospitalInstitution.home.title">Hospital Institutions</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.hospitalInstitution.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/hospital-institution/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.hospitalInstitution.home.createLabel">Create new Hospital Institution</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.hospitalInstitution.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {hospitalInstitutionList && hospitalInstitutionList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('accountHolderName')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.accountHolderName">Account Holder Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('accountHolderName')} />
                </th>
                <th className="hand" onClick={sort('accountNo')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.accountNo">Account No</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('accountNo')} />
                </th>
                <th className="hand" onClick={sort('ifscCode')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.ifscCode">Ifsc Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ifscCode')} />
                </th>
                <th className="hand" onClick={sort('allowedCredit')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.allowedCredit">Allowed Credit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('allowedCredit')} />
                </th>
                <th className="hand" onClick={sort('maxCreditLimit')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.maxCreditLimit">Max Credit Limit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('maxCreditLimit')} />
                </th>
                <th className="hand" onClick={sort('standardCreditLimit')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.standardCreditLimit">Standard Credit Limit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('standardCreditLimit')} />
                </th>
                <th className="hand" onClick={sort('openingBalance')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.openingBalance">Opening Balance</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('openingBalance')} />
                </th>
                <th className="hand" onClick={sort('overDraft')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.overDraft">Over Draft</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('overDraft')} />
                </th>
                <th className="hand" onClick={sort('inactive')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.inactive">Inactive</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('inactive')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampEntry')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.timestampEntry">Timestamp Entry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampEntry')} />
                </th>
                <th className="hand" onClick={sort('lastUpdated')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.lastUpdated">Last Updated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastUpdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.institution">Institution</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.hospitalInstitution.hospital">Hospital</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hospitalInstitutionList.map((hospitalInstitution, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/hospital-institution/${hospitalInstitution.id}`} color="link" size="sm">
                      {hospitalInstitution.id}
                    </Button>
                  </td>
                  <td>{hospitalInstitution.accountHolderName}</td>
                  <td>{hospitalInstitution.accountNo}</td>
                  <td>{hospitalInstitution.ifscCode}</td>
                  <td>{hospitalInstitution.allowedCredit}</td>
                  <td>{hospitalInstitution.maxCreditLimit}</td>
                  <td>{hospitalInstitution.standardCreditLimit}</td>
                  <td>{hospitalInstitution.openingBalance}</td>
                  <td>{hospitalInstitution.overDraft ? 'true' : 'false'}</td>
                  <td>{hospitalInstitution.inactive ? 'true' : 'false'}</td>
                  <td>{hospitalInstitution.active ? 'true' : 'false'}</td>
                  <td>
                    {hospitalInstitution.microSec ? (
                      <TextFormat type="date" value={hospitalInstitution.microSec} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {hospitalInstitution.timestampEntry ? (
                      <TextFormat type="date" value={hospitalInstitution.timestampEntry} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {hospitalInstitution.lastUpdated ? (
                      <TextFormat type="date" value={hospitalInstitution.lastUpdated} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{hospitalInstitution.cursor}</td>
                  <td>
                    {hospitalInstitution.institution ? (
                      <Link to={`/institution/${hospitalInstitution.institution.id}`}>{hospitalInstitution.institution.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {hospitalInstitution.hospital ? (
                      <Link to={`/hospital/${hospitalInstitution.hospital.id}`}>{hospitalInstitution.hospital.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/hospital-institution/${hospitalInstitution.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/hospital-institution/${hospitalInstitution.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/hospital-institution/${hospitalInstitution.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.hospitalInstitution.home.notFound">No Hospital Institutions found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={hospitalInstitutionList && hospitalInstitutionList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default HospitalInstitution;
